/* ============================================================
   TASK-329 (owner decision 2026-09-07): the connected-accounts popover/
   bottom-sheet that opens from the provenance line's "N connected" segment
   — ONLY when there is overpricing beyond the chosen plan
   (SocialAddonProvenanceLine already gates the click on
   shouldOfferAccountsSheet(pricing)). Lists every connected account
   (platform + handle), lets the user disconnect one (inline confirm — no
   window.confirm) or jump to the existing connect entry point ("Connect
   another" -> Settings' Connected social accounts tab, which already owns
   the full per-platform connect grid + capacity/OAuth machinery —
   deliberately NOT duplicated here).

   No shared <ConnectedAccountsList>/sheet primitive exists yet in this repo
   (checked Settings.jsx's AccountsTab, Misc.jsx's social-page, and every
   components/*.jsx — see the task's own file:line survey), so this is a
   minimal, self-contained implementation: it fetches its own accounts via
   the SAME api.socialAccounts.list()/disconnect() calls Settings.jsx's
   AccountsTab already uses (no new endpoints), and renders a bottom sheet
   below 768px / a popover anchored to the trigger at/above it, with a
   focus trap + Esc + backdrop-click close (none of which the existing
   <ConfirmDialog> primitive provides either).

   Round 2 fix (JUDGE OBSERVATION, mobile repro at 375px): portalled to
   document.body via createPortal — this component used to render inline in
   the caller's JSX tree (Settings.jsx/Plans.jsx/PlanSelectScreen.jsx), so
   an ancestor with its own `transform`/`filter`/`contain` (any of which
   establishes a new containing block) could silently turn its `position:
   fixed` backdrop/panel into something anchored to THAT ancestor's box
   instead of the viewport — exactly the "half-width, floating mid-screen"
   symptom the judge's screenshot showed. Portalling to <body> removes that
   dependency entirely, independent of whatever container this sheet
   happens to be opened from.
   ============================================================ */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../pack-icons';
import { BrandIcons } from '../pack-icons/brands';
import { api } from '../../services/api/client.js';

const NOT_CONNECTED_STATUSES = new Set(['disconnected', 'revoked']);

function platformLabelOf(p) {
  const v = String(p || '').toLowerCase();
  if (v === 'x') return 'X';
  if (v === 'tiktok') return 'TikTok';
  if (v === 'linkedin') return 'LinkedIn';
  return v ? v.charAt(0).toUpperCase() + v.slice(1) : 'Account';
}

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function ConnectedAccountsSheet({
  open,
  pricing,
  anchorRef,
  onClose,
  onConnectAnother,
  onDisconnected,
}) {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const confirmBtnRef = useRef(null);
  // Round 3 fix: keyed by account id so a Cancel click can restore focus to
  // THAT row's own Disconnect button once the inline confirm pair unmounts
  // (see the confirmingId effect below) — mirrors confirmBtnRef's own role
  // for the opposite transition (Disconnect clicked -> Confirm button).
  const disconnectBtnRefs = useRef(new Map());
  const prevConfirmingIdRef = useRef(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const [accounts, setAccounts] = useState([]);
  // Round 3 fix: starts `true` (not `false`) so the very first-ever open
  // never paints "no accounts connected" for one frame before the
  // open-effect below has had a chance to call loadAccounts() and flip
  // this back on — an empty `accounts` array during that gap looked
  // identical to a genuinely empty list.
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [desktopPos, setDesktopPos] = useState(null);

  const loadAccounts = () => {
    setLoading(true);
    setLoadError(false);
    api.socialAccounts.list()
      .then((res) => {
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        setAccounts(payload?.accounts || []);
      })
      // MINOR 2 fix: a fetch FAILURE is not the same thing as a genuinely
      // empty list — it must show its own explicit error line (never
      // socialAddon.sheetEmpty, which reads as "you have no accounts",
      // not "we couldn't check").
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    setConfirmingId(null);
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Desktop: position as a popover anchored below the trigger segment.
  // Mobile (<768px, see the CSS media query) ignores this and renders a
  // full-width bottom sheet instead. Recomputed on open + resize + scroll
  // (MINOR 5 fix: a scroll on the page — or, via the capture phase, on any
  // scrollable ancestor of the trigger — can move the anchor without ever
  // firing a `resize`, leaving a stale popover position behind).
  //
  // Round 3 fix:
  //  - useLayoutEffect (not useEffect) so the FIRST position is computed
  //    synchronously before the browser paints — otherwise desktop briefly
  //    rendered the full-width `.is-sheet` layout (desktopPos still null on
  //    the first commit) for one frame before flipping to the popover.
  //  - the scroll listener is passive+capture: it never calls
  //    preventDefault, and capture is what lets it see scrolls on any
  //    scrollable ancestor of the trigger, not just window-level scrolls.
  //  - both listeners route through one rAF-throttled scheduler so a fast
  //    scroll/resize can't force a synchronous layout read+setState on
  //    every single event.
  //  - the computed position is only committed to state when it actually
  //    differs from the last one, so a throttled-but-still-frequent
  //    scroll doesn't re-render the sheet every frame for a no-op update.
  //  - `top` is clamped to the viewport and the popover flips to sit ABOVE
  //    the anchor when there isn't enough room below it, instead of
  //    running off the bottom edge.
  useLayoutEffect(() => {
    if (!open) return undefined;
    let rafId = null;
    const reposition = () => {
      rafId = null;
      const anchor = anchorRef?.current;
      if (!anchor || typeof window === 'undefined' || window.innerWidth < 768) {
        setDesktopPos((prev) => (prev === null ? prev : null));
        return;
      }
      const rect = anchor.getBoundingClientRect();
      const width = 340;
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
      // The panel isn't measured yet on the very first (synchronous) pass —
      // fall back to a reasonable estimate; every later reposition (scroll/
      // resize) reads the real rendered height instead.
      const estimatedHeight = panelRef.current?.offsetHeight || 320;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const top = spaceBelow >= estimatedHeight
        ? rect.bottom + 8
        : Math.max(8, rect.top - estimatedHeight - 8);
      setDesktopPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
    };
    const scheduleReposition = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(reposition);
    };
    reposition();
    window.addEventListener('resize', scheduleReposition);
    window.addEventListener('scroll', scheduleReposition, { passive: true, capture: true });
    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', scheduleReposition);
      window.removeEventListener('scroll', scheduleReposition, { capture: true });
    };
  }, [open, anchorRef]);

  // Focus trap + Esc + restore-on-close — ConfirmDialog (this app's only
  // other modal primitive) has none of these, so this sheet builds them all
  // itself (task requirement). Round 2 fix (MINOR 3): keyed ONLY on `open`
  // now — it used to also depend on `accounts`/`confirmingId`, so every
  // list refresh or Disconnect click re-ran the whole effect and yanked
  // focus back to the sheet's first focusable element instead of leaving it
  // where the user's next action put it. The Tab-trap below now reads the
  // CURRENT focusable set live from the DOM on every keypress instead of
  // capturing a stale snapshot at mount, so it still tracks a changing list
  // correctly without needing those deps.
  useEffect(() => {
    if (!open) return undefined;
    const trigger = (typeof document !== 'undefined' && document.activeElement) || null;
    // Round 3 fix: captured alongside `trigger` at OPEN time, before
    // anything the sheet does can change the surrounding page — a stable
    // ancestor to fall back to if `trigger` itself is gone by the time the
    // sheet closes (see the cleanup below).
    const triggerAncestor = trigger?.closest?.('section, [data-plan], .s-card, .card, [role="region"]')
      || trigger?.parentElement
      || null;
    const panel = panelRef.current;
    const focusables = panel ? Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)) : [];
    (focusables[0] || panel)?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const current = panelRef.current ? Array.from(panelRef.current.querySelectorAll(FOCUSABLE_SELECTOR)) : [];
      if (current.length === 0) return;
      const first = current[0];
      const last = current[current.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      // Restore focus to whatever opened the sheet (the "N connected" chip)
      // — without this, focus drops to <body> on close, a dead end for
      // keyboard/screen-reader users.
      //
      // Round 3 fix: that chip only renders while `shouldOfferAccountsSheet`
      // is true — disconnecting the last billed-overage account WHILE the
      // sheet is open (this sheet's own "Disconnect" flow makes exactly
      // that possible) unmounts it, so `trigger` can be a detached node by
      // the time this runs. Fall back to the nearest still-attached
      // ancestor captured above, made programmatically focusable if it
      // isn't already, rather than silently dropping focus to <body>.
      if (trigger && typeof trigger.focus === 'function' && document.body.contains(trigger)) {
        trigger.focus();
      } else if (triggerAncestor && typeof triggerAncestor.focus === 'function' && document.body.contains(triggerAncestor)) {
        if (!triggerAncestor.hasAttribute('tabindex')) triggerAncestor.setAttribute('tabindex', '-1');
        triggerAncestor.focus();
      }
    };
  }, [open]);

  // Clicking "Disconnect" swaps that row's button for the inline Cancel /
  // Confirm-disconnect pair — the ORIGINAL button unmounts in that swap, so
  // without this, focus silently falls back to <body>. Moves focus to the
  // new "Confirm disconnect" button once it exists.
  useEffect(() => {
    if (confirmingId) confirmBtnRef.current?.focus();
  }, [confirmingId]);

  // Round 3 fix: the reverse transition — Cancel (or a completed disconnect)
  // unmounts the inline Confirm/Cancel pair and brings the row's plain
  // "Disconnect" button back (or removes the row entirely, in which case
  // the ref lookup below simply misses and this is a no-op). Tracks the
  // PREVIOUS confirmingId in a ref rather than a second piece of state so
  // this also fires correctly if the same row is opened and cancelled
  // twice in a row (confirmingId genuinely transitions through null each
  // time, unlike a "last cancelled id" state that wouldn't change value).
  useEffect(() => {
    const prev = prevConfirmingIdRef.current;
    prevConfirmingIdRef.current = confirmingId;
    if (confirmingId === null && prev !== null) {
      disconnectBtnRefs.current.get(prev)?.focus();
    }
  }, [confirmingId]);

  if (!open || typeof document === 'undefined') return null;

  const handleDisconnect = async (account) => {
    setDisconnectingId(account.id);
    setActionError(null);
    try {
      await api.socialAccounts.disconnect(account.id);
      setAccounts((prev) => prev.filter((a) => a.id !== account.id));
      setConfirmingId(null);
      onDisconnected?.(account);
    } catch (err) {
      setActionError(err?.message || t('settings.disconnectFailed', { defaultValue: 'Could not disconnect. Try again.' }));
    } finally {
      setDisconnectingId(null);
    }
  };

  const connectedAccounts = accounts.filter((a) => {
    const status = typeof a?.status === 'string' ? a.status.toLowerCase() : '';
    return !NOT_CONNECTED_STATUSES.has(status);
  });

  const panelStyle = desktopPos
    ? { position: 'fixed', top: desktopPos.top, left: desktopPos.left, width: 340 }
    : undefined;

  return createPortal((
    <div className="sa-sheet-backdrop" role="presentation" onClick={onClose}>
      <div
        ref={panelRef}
        className={`sa-sheet-panel ${desktopPos ? 'is-popover' : 'is-sheet'}`}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sa-sheet-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sa-sheet-grabber" aria-hidden="true" />
        <div className="sa-sheet-head">
          <div>
            <h3 id="sa-sheet-title">{t('socialAddon.sheetTitle')}</h3>
            {pricing?.provenanceLine && <p className="sa-sheet-provenance">{pricing.provenanceLine}</p>}
          </div>
          <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={onClose} aria-label={t('socialAddon.sheetClose')}>
            <I.x size={13} />
          </button>
        </div>

        <div className="sa-sheet-body">
          {loading ? (
            <div className="muted" style={{ fontSize: 13 }}>{t('settings.loadingAccounts', { defaultValue: 'Loading…' })}</div>
          ) : loadError ? (
            <div className="sa-sheet-error" role="alert">
              <p style={{ margin: '0 0 8px' }}>{t('socialAddon.sheetLoadError')}</p>
              <button type="button" className="btn btn-ghost btn-sm" onClick={loadAccounts}>
                {t('socialAddon.sheetRetry')}
              </button>
            </div>
          ) : connectedAccounts.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>{t('socialAddon.sheetEmpty')}</p>
          ) : (
            <ul className="sa-sheet-list">
              {connectedAccounts.map((a) => {
                const platform = (a.platform || '').toLowerCase();
                const label = a.displayName || a.externalAccountId || platformLabelOf(platform);
                const isConfirming = confirmingId === a.id;
                return (
                  <li key={a.id} className="sa-sheet-row">
                    <span className={`ob-net-ic-${platform}`} style={{ width: 30, height: 30, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
                      {BrandIcons[platform]?.({ size: 15 })}
                    </span>
                    <div className="sa-sheet-row-text">
                      <div className="sa-sheet-row-name">{label}</div>
                      <div className="sa-sheet-row-sub">{platformLabelOf(platform)}</div>
                    </div>
                    {isConfirming ? (
                      <div className="sa-sheet-row-confirm">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmingId(null)} disabled={disconnectingId === a.id}>
                          {t('socialAddon.sheetDisconnectCancel')}
                        </button>
                        <button type="button" ref={confirmBtnRef} className="btn btn-danger btn-sm" onClick={() => handleDisconnect(a)} disabled={disconnectingId === a.id}>
                          {disconnectingId === a.id ? '…' : t('socialAddon.sheetDisconnectConfirm')}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        ref={(el) => {
                          if (el) disconnectBtnRefs.current.set(a.id, el);
                          else disconnectBtnRefs.current.delete(a.id);
                        }}
                        onClick={() => setConfirmingId(a.id)}
                      >
                        {t('socialAddon.sheetDisconnect')}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {!loadError && actionError && <p className="sa-sheet-error" role="alert">{actionError}</p>}
        </div>

        {/* onConnectAnother is optional — omitted on a surface with no safe
            connect entry point to send the user to (e.g. PlanSelectScreen,
            reachable only pre-subscription; navigating to Settings there can
            bounce straight back via RequireActiveBilling's own gate). */}
        {onConnectAnother && (
          <div className="sa-sheet-footer">
            <button type="button" className="btn btn-soft btn-sm" onClick={onConnectAnother}>
              {t('socialAddon.sheetConnectAnother')}
            </button>
          </div>
        )}
      </div>
    </div>
  ), document.body);
}
